import React, { useContext } from 'react';
import {
  LayoutDashboard,
  Users,
  MapPin,
  Package,
  TrendingUp,
  BookOpen,
  ShieldCheck,
  LogOut,
  Building2,
  UserCog,
  HardHat
} from "lucide-react";
import { NavLink, useNavigate } from 'react-router-dom'; // 🌟 useNavigate 추가
import { clsx } from "clsx";
import { AuthContext } from '@context'; 

export function Sidebar({ isOpen }) {
  const { auth, logout } = useContext(AuthContext);
  const navigate = useNavigate(); // 🌟 페이지 이동을 위한 훅
  
  const userRole = auth?.role || '';

  const allNavItems = [
    { name: "대시보드", icon: LayoutDashboard, path: "/dashboard", roles: ['ROLE_ADMIN', 'ROLE_USER'] },
    { name: "회원 관리", icon: UserCog, path: "/member-management", roles: ['ROLE_ADMIN'] },
    { name: "수행요원 관리", icon: HardHat, path: "/staff-management", roles: ['ROLE_ADMIN'] },
    { name: "사업장 관리", icon: Building2, path: "/company", roles: ['ROLE_ADMIN'] },
    { name: "사업장 배정 관리", icon: Users, path: "/company-assignment", roles: ['ROLE_ADMIN', 'ROLE_USER'] },
    { name: "방문 관리", icon: MapPin, path: "/visit-management", roles: ['ROLE_ADMIN', 'ROLE_USER'] },
    { name: "산출물 관리", icon: Package, path: "/deliverables", roles: ['ROLE_ADMIN', 'ROLE_USER'] },
    { name: "진행 관리", icon: TrendingUp, path: "/progress", roles: ['ROLE_ADMIN'] },
    { name: "자료실", icon: BookOpen, path: "/reference", roles: ['ROLE_ADMIN', 'ROLE_USER'] }
  ];

  const navItems = allNavItems.filter((item) => item.roles.includes(userRole));

  // 🌟 로고 클릭 핸들러: 권한에 따라 알맞은 홈 화면으로 이동
  const handleLogoClick = () => {
    if (userRole === 'ROLE_ADMIN') {
      navigate('/dashboard');
    } else if (userRole === 'ROLE_USER') {
      navigate('/dashboard');
    } else {
      navigate('/');
    }
  };

  return (
    <div
      className={clsx(
        "flex h-screen w-64 flex-col bg-slate-900 text-white overflow-y-auto transition-transform duration-300 ease-in-out",
        "h-full w-full"
      )}>
      
      {/* 🌟 Logo Area: 클릭 이벤트 및 커서 스타일(cursor-pointer) 추가 */}
      <div 
        onClick={handleLogoClick}
        className="flex items-center gap-3 px-6 py-6 border-b border-slate-800 shrink-0 cursor-pointer hover:bg-slate-800/50 transition-colors"
      >
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-600 shadow-lg shadow-blue-900/50">
          <ShieldCheck className="h-6 w-6 text-white" />
        </div>
        <div>
          {/* 로고 이름 크기도 약간 조정 */}
          <h1 className="text-lg font-bold leading-none tracking-tight">SafetyPortal</h1>
          <span className="text-xs text-slate-400 font-medium">
            {auth?.name ? `${auth.name}님 환영합니다` : '기업용 솔루션'}
          </span>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 space-y-1.5 px-3 py-6 overflow-y-auto">
        {navItems.map((item) =>
        <NavLink
          key={item.name}
          to={item.path}
          className={({ isActive }) =>
          clsx(
            // 🌟 폰트 크기를 text-sm 에서 text-base(기본) 로 키우고, 여백(py)도 살짝 넓힘
            "flex items-center gap-3 rounded-lg px-3 py-3.5 text-base font-medium transition-all duration-200 group",
            isActive ?
            "bg-blue-600 text-white shadow-md shadow-blue-900/30" :
            "text-slate-400 hover:bg-slate-800 hover:text-white hover:translate-x-1"
          )
          }>
          {({ isActive }) => (
            <>
              {/* 아이콘 크기도 약간 키움 (h-5 -> h-5.5 정도 느낌으로) */}
              <item.icon className={clsx("h-[22px] w-[22px] transition-colors", isActive ? "text-white" : "text-slate-500 group-hover:text-white")} />
              {item.name}
            </>
          )}
          </NavLink>
        )}
      </nav>

      {/* Bottom Footer */}
      <div className="p-4 border-t border-slate-800 shrink-0">
        <button
          onClick={() => logout()}
          // 로그아웃 버튼 글씨도 통일감 있게 키움
          className="flex w-full items-center gap-2 rounded-lg bg-slate-800/50 px-4 py-3 text-base text-slate-400 hover:bg-slate-800 hover:text-white transition-all justify-center mb-4 border border-slate-800 hover:border-slate-700">
          <LogOut className="h-5 w-5" />
          로그아웃
        </button>
        <p className="text-[11px] text-slate-600 text-center uppercase tracking-wider">
          © 2026 Industrial Safety
        </p>
      </div>
    </div>
  );
}